import * as React from 'react';
import i18n from '../i18n';
import { api, setAccessToken, onAuthEvent, refreshSession } from '../api/client';
import { ROLE_BY_ID, RoleKey } from './roles';

export interface AuthUser {
	id: number;
	name: string;
	email: string;
	role_id: number;
	company_id: number;
	role: RoleKey | null;
	Roles?: { id: number; name: string };
	/** User's stored language preference (synced via PATCH /users/me/preferences). */
	preferred_locale?: string | null;
}

interface AuthState {
	user: AuthUser | null;
	status: 'loading' | 'anonymous' | 'authenticated';
}

interface AuthCtx extends AuthState {
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	refresh: () => Promise<void>;
}

const Ctx = React.createContext<AuthCtx | null>(null);
const STORAGE_KEY = 'sparts.session.user';

function toAuthUser(raw: any): AuthUser | null {
	if (!raw || typeof raw !== 'object') return null;
	const role = ROLE_BY_ID[raw.role_id] || null;
	return { ...raw, role } as AuthUser;
}

function readPersisted(): AuthUser | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}
function writePersisted(u: AuthUser | null) {
	try {
		if (u) localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
		else localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* localStorage may be disabled — ignore */
	}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	// Optimistic restore from localStorage so a page reload with a valid
	// session doesn't flash through 'loading → anonymous → login redirect'
	// before the background refresh completes.
	const persisted = React.useMemo(readPersisted, []);
	const [state, setState] = React.useState<AuthState>(() => ({
		user: persisted,
		status: persisted ? 'authenticated' : 'loading',
	}));

	// Mirror state.user into a ref so the auth-event listener can read the
	// latest value without being a dep on a useEffect. This kept the listener
	// effect stable (subscribed exactly once for the lifetime of the provider)
	// instead of re-subscribing on every render that changed state.user — the
	// previous arrangement opened a tiny re-subscribe race window during
	// concurrent /auth/refresh + listener fan-out.
	const userRef = React.useRef<AuthUser | null>(state.user);
	React.useEffect(() => {
		userRef.current = state.user;
	}, [state.user]);

	// When the auth state settles with a user that has a preferred_locale,
	// sync i18n to it so the user's cross-device language choice takes
	// precedence over whatever localStorage held on this device. This is the
	// "smooth across devices" UX you asked for: log in on phone in EN,
	// switch to AR on laptop, next phone session opens in AR automatically.
	React.useEffect(() => {
		const target = state.user?.preferred_locale;
		if (!target) return;
		const current = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2);
		if (target.slice(0, 2) !== current) {
			i18n.changeLanguage(target);
		}
	}, [state.user?.preferred_locale]);

	const setAuthed = React.useCallback(
		(token: string | null, user: AuthUser | null) => {
			setAccessToken(token);
			writePersisted(user);
			setState({ user, status: user && token ? 'authenticated' : 'anonymous' });
		},
		[]
	);

	const refresh = React.useCallback(async () => {
		const { accessToken: token, user } = await refreshSession();
		setAuthed(token, toAuthUser(user));
	}, [setAuthed]);

	const login = React.useCallback(
		async (email: string, password: string) => {
			const { data } = await api.post('/auth/login', { email, password });
			setAuthed(data.accessToken, toAuthUser(data.user));
		},
		[setAuthed]
	);

	const logout = React.useCallback(async () => {
		try {
			await api.post('/auth/logout');
		} catch {
			/* fall through — local state still cleared */
		}
		setAuthed(null, null);
	}, [setAuthed]);

	// Bootstrap session ONCE on mount via the shared single-flight refresh.
	// If the cookie is still valid we get a fresh access token; otherwise
	// flushed to anonymous and the router bounces to /login.
	React.useEffect(() => {
		let cancelled = false;
		refreshSession().then(({ accessToken: token, user }) => {
			if (cancelled) return;
			setAuthed(token, toAuthUser(user));
		});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// React to events emitted by the axios interceptor's silent refresh
	// (when a 401 was caught on a non-auth endpoint). Subscribed ONCE — the
	// listener reads the latest user via userRef so we don't have to put
	// state.user in the deps.
	React.useEffect(() => {
		const unsubscribe = onAuthEvent((event, payload: any) => {
			if (event === 'auth:logout') {
				setAuthed(null, null);
				return;
			}
			if (event === 'auth:refreshed' && payload?.accessToken) {
				const u = payload.user ? toAuthUser(payload.user) : userRef.current;
				setAuthed(payload.accessToken, u);
			}
		});
		return () => {
			unsubscribe();
		};
	}, [setAuthed]);

	const value: AuthCtx = { ...state, login, logout, refresh };
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
	const v = React.useContext(Ctx);
	if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
	return v;
}
